import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';

export interface RateLimitInfo {
  endpoint: string;
  retryAfter: number;
  limit: number;
  windowMs: number;
  blockedUntil: Date;
}

@Injectable({
  providedIn: 'root'
})
export class RateLimitService {
  private rateLimitedEndpoints = new Map<string, RateLimitInfo>();
  private rateLimitSubject = new BehaviorSubject<RateLimitInfo | null>(null);
  
  rateLimitStatus$ = this.rateLimitSubject.asObservable();

  constructor() {}

  /**
   * Check if an error is a rate limit error
   */
  isRateLimitError(error: HttpErrorResponse): boolean {
    return error.status === 429;
  }

  /**
   * Handle rate limit error
   */
  handleRateLimitError(error: HttpErrorResponse, endpoint: string): void {
    if (!this.isRateLimitError(error)) {
      return;
    }

    const errorBody = error.error;
    const retryAfter = errorBody?.details?.retryAfter || 60;
    const limit = errorBody?.details?.limit || 0;
    const windowMs = errorBody?.details?.windowMs || 60000;

    const rateLimitInfo: RateLimitInfo = {
      endpoint,
      retryAfter,
      limit,
      windowMs,
      blockedUntil: new Date(Date.now() + (retryAfter * 1000))
    };

    this.rateLimitedEndpoints.set(endpoint, rateLimitInfo);
    this.rateLimitSubject.next(rateLimitInfo);

    // Automatically remove the rate limit after it expires
    setTimeout(() => {
      this.rateLimitedEndpoints.delete(endpoint);
      if (this.rateLimitedEndpoints.size === 0) {
        this.rateLimitSubject.next(null);
      }
    }, retryAfter * 1000);
  }

  /**
   * Check if an endpoint is currently rate limited
   */
  isEndpointRateLimited(endpoint: string): boolean {
    const info = this.rateLimitedEndpoints.get(endpoint);
    if (!info) {
      return false;
    }

    // Check if the rate limit has expired
    if (new Date() > info.blockedUntil) {
      this.rateLimitedEndpoints.delete(endpoint);
      return false;
    }

    return true;
  }

  /**
   * Get rate limit info for an endpoint
   */
  getRateLimitInfo(endpoint: string): RateLimitInfo | undefined {
    return this.rateLimitedEndpoints.get(endpoint);
  }

  /**
   * Clear all rate limits (for testing)
   */
  clearRateLimits(): void {
    this.rateLimitedEndpoints.clear();
    this.rateLimitSubject.next(null);
  }

  /**
   * Format retry after time for display
   */
  formatRetryAfter(seconds: number): string {
    if (seconds < 60) {
      return `${seconds} seconds`;
    } else if (seconds < 3600) {
      const minutes = Math.ceil(seconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else {
      const hours = Math.ceil(seconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
  }
}